import React, {Component} from 'react';
import {IntlProvider} from 'react-intl';
import {inject, observer} from 'mobx-react';
import {toJS} from 'mobx';
import {graphql} from '@apollo/client/react/hoc/graphql';
import {isEmpty} from 'lodash';
import {ThemeProvider} from 'styled-components';
import NotificationSystem from 'react-notification-system';

import NotFoundComponent from 'components/NotFoundComponent';
import Header from 'components/Header';
import StickyHeader from 'components/StickyHeader';
import ErrorBoundary from 'components/ErrorBoundary';
import ScrollRestoration from '@swipe-guide/clients-shared/components/ScrollRestoration';
import ToastNotifications from '@swipe-guide/clients-shared/components/ToastNotifications';
import CookieBanner from '@swipe-guide/clients-shared/components/CookieBanner';
import LogoutDialog from '@swipe-guide/clients-shared/components/LogoutDialog';

import {Platform} from '@swipe-guide/clients-shared/api/platform/queries';

import views from 'config/views';
import {SidePanelProvider} from '@swipe-guide/clients-shared/components/SidePanel';

//userpilot
import {UserpilotTrigger} from '@swipe-guide/clients-shared/components/UserpilotTrigger';
import {USERPILOT_TOKEN, USERPILOT_APPLICATION_NAME} from '@swipe-guide/clients-shared/constants';

@inject('store')
@graphql(Platform)
@observer
class App extends Component {
  UNSAFE_componentWillMount() {
    const {
      store: {auth, platform}
    } = this.props;
    Promise.all([
      auth.getCurrentUser(),
      platform.fetchAvailableLanguages(),
      platform.fetchPlatformFlags(),
      platform.fetchDevelopmentFeatureFlags()
    ]);
  }

  UNSAFE_componentWillReceiveProps(nextProps) {
    if (nextProps.data && nextProps.data.platform) {
      const {
        store: {app, platform},
        data
      } = nextProps;
      const {organization, domain} = data.platform;
      platform.setOrganization(organization);
      platform.setPlatformId(data.platform.id);
      platform.setPlatformName(data.platform.name);
      platform.setAssetsFolder(data.platform.assetsFolder);
      platform.setDefaultDomainSlug(data.platform.defaultDomain);
      platform.setPlatformSlug(data.platform.subdomain);
      platform.setDomain(domain);
      platform.setIconsShowHintLabel(data.platform.showStepNoteIconLabel);
      app.setTheme({...domain.theme});
    }
  }

  componentDidMount = () => {
    const {
      store: {
        platform: {addOnlineStatusListener},
        app: {setViews}
      }
    } = this.props;

    addOnlineStatusListener();
    setViews(views);
  };

  render() {
    const {store, data} = this.props;
    const {loading: dataLoading} = data;
    const {router, auth, app, platform, notification, cookiePreference} = store;
    const {getCurrentComponent, theme, showLogoutDialog} = app;
    const {setNotificationRef} = notification;
    const {
      acceptAllCookies,
      openCookiePreferencesDialog,
      setCookiePreferenceOption,
      cookiePreference: cookiePreferenceOption,
      hideCookiePreferencesDialog
    } = cookiePreference;
    const {activeLocale, localeMessages} = store.platform;

    if (!router.currentRoute || platform.notFound) {
      return <NotFoundComponent noIntl />;
    }

    const currentComponent = getCurrentComponent(store);
    const loading =
      !platform.loaded ||
      auth.authing ||
      dataLoading ||
      this.theme === null ||
      !platform.availableLanguages ||
      isEmpty(localeMessages);

    const header = props => <Header stick={props.stick} inInstruction={props.isInInstruction} />;

    if (loading) {
      return null;
    }
    return (
      <IntlProvider locale={activeLocale} messages={localeMessages[activeLocale]} textComponent="span">
        <ErrorBoundary>
          <UserpilotTrigger token={USERPILOT_TOKEN} application={USERPILOT_APPLICATION_NAME} />
          <SidePanelProvider>
            {theme.fontStylesheet && <style>{theme.fontStylesheet}</style>}
            <ThemeProvider theme={toJS(theme)}>
              <StickyHeader loading={loading} header={header}>
                <ScrollRestoration />
                {currentComponent}
              </StickyHeader>
            </ThemeProvider>
            <NotificationSystem ref={ref => setNotificationRef(ref)} />
            <ToastNotifications isDesktop />
            <CookieBanner
              openDialog={openCookiePreferencesDialog}
              acceptAllCookies={acceptAllCookies}
              cookiePreference={cookiePreferenceOption}
              setCookiePreferenceOption={setCookiePreferenceOption}
              goToPrivacyPage={() => {
                hideCookiePreferencesDialog();
                router.goTo(views.privacy, {});
              }}
            />
            <LogoutDialog isDesktop isOpen={showLogoutDialog} logout={auth.logout} />
          </SidePanelProvider>
        </ErrorBoundary>
      </IntlProvider>
    );
  }
}

export default App;
